<script setup lang="ts">
// ================================
// IMPORTS
// ================================
import { computed, inject, ref, watch } from "vue"
import { useI18n } from "vue-i18n"
import { useRoute, useRouter } from "vue-router"
import { useClipboard, useEventBus } from "@vueuse/core"
import type { NotificationEvent } from "@types"
import { SIDEBAR_CONTEXT_KEY } from "@core/layouts/sidebar-context"
import type { SidebarMenuItem } from "@core/layouts/sidebars/sidebar-menu.types"
import { SOURCES_TYPES } from "@core/constants"
import { useResponsive } from "@core/composables"
import { formatIdSource } from "@core/helpers"
import { useAuthStore } from "@core/stores/auth.store"
import { useBuildingStore } from "@core/stores/building-v2.store"
import { useSourceStore } from "@core/stores/source.store"

const { t } = useI18n({
    messages: {
        fr: {
            menu: {
                infoGeneral: "Info. générales",
                data: "Données",
                alarm: "Alarmes",
                factures: "Factures",
            },
            header: {
                backToBuilding: "Retour au bâtiment",
                sourceId: "Identifiant source",
                remove: "Supprimer",
                copyIdsource: "Cliquer pour copier",
                copySuccess: "Numéro copié dans le presse-papier",
            },
        },
    },
})

// ================================
// COMPOSABLES
// ================================
const route = useRoute()
const router = useRouter()
const sourceStore = useSourceStore()
const buildingStore = useBuildingStore()
const authStore = useAuthStore()
const bus = useEventBus<NotificationEvent>("notification")
const { copy } = useClipboard()
const sidebarContext = inject(SIDEBAR_CONTEXT_KEY)
const { isMobile } = useResponsive()

/** Referme la sidebar sur mobile après navigation depuis l’en-tête. */
function onCollapseSidebarIfMobile() {
    if (!isMobile.value) return
    sidebarContext?.collapse?.()
}

// ================================
// STATE
// ================================
const ownerMenuRef = ref<{ toggle: (e: Event) => void } | null>(null)
/** Dialogue de confirmation de suppression (logique dans SourceDeleteDialog). */
const removeConfirmVisible = ref(false)

// ================================
// COMPUTED
// ================================
const getSourceId = computed(() => route.params._id_source as string)
const getCollapsed = computed(() => sidebarContext?.collapsed?.value ?? false)

const NON_DELETABLE_SOURCE_TYPES = [
    SOURCES_TYPES.METEOFRANCE,
    SOURCES_TYPES.OPENMETEO,
    SOURCES_TYPES.RTE,
]

// ================================
// WATCHERS
// ================================
/**
 * Charge la source au changement d'id dans la route.
 */
watch(
    getSourceId,
    (id) => {
        if (!id) return
        const current = sourceStore.getSourceById(id)
        if (current) return
        sourceStore.fetchSourceById({ _id_source: id })
    },
    { immediate: true }
)

const getIsSourceLoading = computed(() =>
    Boolean(getSourceId.value && sourceStore.isFetchingSourceById)
)
const getSource = computed(() =>
    getSourceId.value ? sourceStore.getSourceById(getSourceId.value) : undefined
)
const getSourceName = computed(() => getSource.value?.name ?? "-")
const getSourceIconType = computed(
    () => getSource.value?.type ?? getSource.value?.idtype ?? "default"
)
const getSourceIdsource = computed(() => getSource.value?.idsource ?? "")
const getSourceType = computed(
    () => (getSource.value?.type ?? getSource.value?.idtype ?? "") as string
)
const getSourceIdsourceFormatted = computed(() =>
    formatIdSource(getSourceIdsource.value, getSourceType.value)
)
const getSourceSubtitle = computed(() => {
    const s = getSource.value
    if (!s) return ""
    const type = (s.type ?? s.idtype ?? "").toString().toUpperCase()
    return type ? `${type} : ${s.idsource ?? ""}` : (s.idsource ?? "")
})

function copyIdsource() {
    const id = getSourceIdsource.value
    if (!id) return
    copy(id)
    bus.emit({
        severity: "success",
        title: t("header.copySuccess"),
        life: 3000,
    })
}

/** Lien vers le bâtiment si le store bâtiment contient cette source. */
const getBuildingPath = computed(() => {
    const id = getSourceId.value
    const b = buildingStore.building
    if (!id || !b?.sources?.length) return null
    const hasSource = b.sources.some(
        (s: { _id_source?: string }) => s._id_source === id
    )
    return hasSource ? `/building/${b._id_building}/compteur` : null
})

/**
 * Affiche le menu propriétaire (Supprimer) si l'utilisateur peut supprimer cette source.
 * Conditions (aligné legacy) : pas type météo/RTE ET (admin entreprise client Homeys OU propriétaire non-client Homeys).
 */
const hasOwnerMenu = computed(() => {
    const s = getSource.value
    if (!s) return false
    const type = (s.type ?? s.idtype ?? "") as string
    if (NON_DELETABLE_SOURCE_TYPES.includes(type)) return false
    const isCompanyAdminAndHomeys =
        authStore.isCompanyAdmin && authStore.isClientHomeys
    const isOwnerNotHomeys =
        (s.rights?.includes?.("owns") ?? false) && !authStore.isClientHomeys
    return isCompanyAdminAndHomeys || isOwnerNotHomeys
})

const ownerMenuModel = computed(() => [
    {
        label: t("header.remove"),
        icon: "pi pi-trash",
        command: () => {
            onOpenRemoveConfirm()
        },
    },
])

function onToggleOwnerMenu(event: Event) {
    ownerMenuRef.value?.toggle(event)
}

/**
 * Ouvre le dialogue de confirmation avant suppression de la source courante.
 */
function onOpenRemoveConfirm(): void {
    const s = getSource.value
    const id = getSourceId.value
    if (!s || !id) return
    removeConfirmVisible.value = true
}

/**
 * Retire le préfixe `base` du routeur pour comparer un chemin d’historique.
 */
function stripRouterBaseFromPath(fullPath: string): string {
    const base = (router.options.history.base || "/").replace(/\/$/, "")
    let p = fullPath.split("?")[0] ?? ""
    if (base && base !== "/" && p.startsWith(base)) {
        p = p.slice(base.length) || "/"
    }
    return p.startsWith("/") ? p : `/${p}`
}

/**
 * Indique si le chemin correspond à une fiche source `/source/:id/...` (pas la liste `/source`).
 */
function isSourceEntityHistoryPath(path: string | null | undefined): boolean {
    if (!path || typeof path !== "string") return false
    const p = stripRouterBaseFromPath(path)
    return /^\/source\/[^/]+/.test(p)
}

/**
 * Après suppression : retour arrière, sauf si la page précédente est une autre fiche source → accueil.
 */
function navigateAfterDeleteSuccess(): void {
    const state = window.history.state as { back?: string | null } | undefined
    const back = state?.back
    if (isSourceEntityHistoryPath(back ?? undefined)) {
        router.push({ name: "Home" })
        return
    }
    router.back()
}

/**
 * Après suppression réussie (toast géré par SourceDeleteDialog) : navigation.
 */
function onSourceDeleteSuccess(): void {
    navigateAfterDeleteSuccess()
}

// ================================
// CONFIG MENU SOURCE (aligné sur legacy pages/source/_id.vue)
// ================================
const menuModel = computed<SidebarMenuItem[]>(() => {
    const id = getSourceId.value
    if (!id) return []
    const base = `/source/${id}`
    const items: SidebarMenuItem[] = [
        {
            key: "info-general",
            label: t("menu.infoGeneral"),
            icon: "pi pi-info-circle",
            route: { path: `${base}/info-general` },
        },
        {
            key: "data",
            label: t("menu.data"),
            icon: "pi pi-chart-line",
            route: { path: `${base}/data` },
        },
    ]
    // « Alarmes » : réservé à l'offre Energy Management, comme la page qu'il
    // ouvre et comme les routes d'api-metier (DEV-2252, « pas d'alarmes si pas
    // de suivi »). L'entrée était inconditionnelle et menait, hors EM, à une
    // redirection silencieuse vers les informations générales.
    if (authStore.hasOfferEnergyManager) {
        items.push({
            key: "alarmes",
            label: t("menu.alarm"),
            icon: "pi pi-bell",
            route: { path: `${base}/alarme` },
        })
    }
    // Onglet « Factures » (DEV-714) : visible si la company a le module
    // analyse-facture ET si l'utilisateur est ADMIN de l'entreprise (R4). Le
    // contenu reste invoice-only ; la visibilité par compteur et l'accès sont
    // décidés côté api-metier (le front ne fait que l'UX).
    //
    // ── LANCEMENT SHADOW (DEV-714) ──────────────────────────────────────────
    // L'espace est déployé et reste joignable par URL directe
    // (`/courtage/mes-factures` et `${base}/factures`) — route + guard
    // entitlement/admin CONSERVÉS. Mais on ne surface AUCUN lien dans la nav
    // tant que la validation équipe n'est pas faite : un admin entitlé (ex.
    // Crous) ne voit donc pas l'onglet et ne tombe pas dessus.
    // → Pour la mise à disposition réelle : passer ESPACE_FACTURES_VISIBLE à true
    //   (mise à dispo instantanée, le gate entitlement/admin reprend la main).
    const ESPACE_FACTURES_VISIBLE: boolean = false
    if (
        ESPACE_FACTURES_VISIBLE &&
        authStore.hasModuleAnalyseFacture &&
        authStore.isCompanyAdmin
    ) {
        items.push({
            key: "factures",
            label: t("menu.factures"),
            icon: "pi pi-file",
            route: { path: `${base}/factures` },
        })
    }
    return items
})
</script>

<template>
    <BaseSidebar :model="menuModel" :loading="getIsSourceLoading">
        <template #top>
            <!-- Chargement : skeletons infos source -->
            <div
                v-if="getIsSourceLoading"
                class="flex flex-col gap-3 px-2 pb-2 mb-2 border-b border-surface-200"
                data-sidebar-toggle
            >
                <div class="flex items-center gap-2">
                    <BaseSkeleton
                        shape="circle"
                        size="2.5rem"
                        class="shrink-0"
                    />
                    <BaseSkeleton width="60%" height="1.25rem" />
                </div>
                <BaseSkeleton width="100%" height="1rem" />
            </div>
            <!-- Mode replié : icône source + lien bâtiment + menu ... -->
            <div
                v-else-if="getSource && getCollapsed"
                class="flex flex-col items-center gap-2 px-2 pb-2 mb-2 border-b border-surface-200"
                data-sidebar-toggle
            >
                <BaseSourceIcon
                    :type="getSourceIconType"
                    :size="28"
                    class="shrink-0"
                />
                <BaseButton
                    v-if="getBuildingPath"
                    v-tooltip.right="t('header.backToBuilding')"
                    icon="pi pi-chevron-left"
                    link
                    rounded
                    class="h-10 w-10 min-w-10 shrink-0 p-0"
                    :aria-label="t('header.backToBuilding')"
                    @click="
                        () => {
                            if (getBuildingPath) {
                                router.push(getBuildingPath)
                                onCollapseSidebarIfMobile()
                            }
                        }
                    "
                />
                <BaseButton
                    v-if="hasOwnerMenu"
                    icon="pi pi-ellipsis-v"
                    text
                    rounded
                    severity="secondary"
                    class="h-10 w-10 min-w-10 shrink-0 p-0"
                    aria-label="Menu source"
                    @click="onToggleOwnerMenu"
                />
            </div>
            <!-- Mode étendu : icône, nom, idsource, lien bâtiment, menu ... -->
            <div
                v-else-if="getSource && !getCollapsed"
                class="flex flex-col gap-3 px-2 pb-2 mb-2 border-b border-surface-200"
                data-sidebar-toggle
            >
                <div class="flex items-start justify-between gap-2 min-w-0">
                    <div class="flex items-center gap-2 min-w-0">
                        <BaseSourceIcon
                            :type="getSourceIconType"
                            :size="40"
                            class="shrink-0"
                        />
                        <div class="flex flex-col min-w-0">
                            <h2
                                class="text-base font-semibold text-surface-900 truncate"
                                :title="getSourceName"
                            >
                                {{ getSourceName ?? "-" }}
                            </h2>
                            <span
                                v-if="getSourceSubtitle"
                                class="text-sm text-surface-600 truncate flex items-baseline gap-0.5 min-w-0"
                                :title="getSourceSubtitle"
                            >
                                <template
                                    v-if="getSource?.type ?? getSource?.idtype"
                                >
                                    {{
                                        (
                                            getSource?.type ??
                                            getSource?.idtype ??
                                            ""
                                        )
                                            .toString()
                                            .toUpperCase()
                                    }}
                                    : &#32;
                                </template>
                                <span
                                    v-if="getSourceIdsourceFormatted"
                                    v-tooltip="t('header.copyIdsource')"
                                    role="button"
                                    tabindex="0"
                                    class="cursor-pointer truncate min-w-0 hover:underline hover:text-primary focus:outline-none focus:underline focus:text-primary"
                                    @click="copyIdsource"
                                    @keydown.enter="copyIdsource"
                                    @keydown.space.prevent="copyIdsource"
                                >
                                    {{ getSourceIdsourceFormatted }}
                                </span>
                            </span>
                        </div>
                    </div>
                    <BaseButton
                        v-if="hasOwnerMenu"
                        icon="pi pi-ellipsis-v"
                        text
                        rounded
                        size="small"
                        severity="secondary"
                        class="shrink-0"
                        aria-label="Menu source"
                        @click="onToggleOwnerMenu"
                    />
                </div>
                <BaseButton
                    v-if="getBuildingPath"
                    :label="t('header.backToBuilding')"
                    link
                    size="small"
                    icon="pi pi-chevron-left"
                    icon-pos="left"
                    class="p-0 justify-start text-sm w-fit"
                    @click="
                        () => {
                            if (getBuildingPath) {
                                router.push(getBuildingPath)
                                onCollapseSidebarIfMobile()
                            }
                        }
                    "
                />
            </div>
            <!-- Menu ... (Supprimer), rendu pour les deux modes -->
            <BaseMenu
                v-if="getSource && hasOwnerMenu"
                ref="ownerMenuRef"
                :model="ownerMenuModel"
                popup
            />
        </template>
    </BaseSidebar>
    <SourceDeleteDialog
        v-model:visible="removeConfirmVisible"
        :source-id="getSourceId"
        :source-label="getSource?.idsource"
        @success="onSourceDeleteSuccess"
    />
</template>
